import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Scroll-driven WebGL backdrop for the landing page.
// Everything here is procedural — no model or texture is downloaded.

const CYAN = new THREE.Color('#5FE0FB');
const VIOLET = new THREE.Color('#8B5CF6');

const vertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uAmp;
  varying vec3 vNormal;
  varying float vWave;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec3 p = position;
    float w =
      sin(p.x * 2.6 + uTime * 0.7) *
      sin(p.y * 2.2 - uTime * 0.5) *
      sin(p.z * 2.9 + uTime * 0.6);
    vWave = w;
    p += normal * w * uAmp;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  varying vec3 vNormal;
  varying float vWave;

  void main() {
    // Rim light: faces turned away from the viewer glow.
    float fresnel = pow(1.0 - abs(dot(normalize(vNormal), vec3(0.0, 0.0, 1.0))), 2.2);
    vec3 col = mix(uColorA, uColorB, clamp(vWave * 0.5 + 0.5, 0.0, 1.0));
    float alpha = fresnel * 0.9 + 0.06;
    gl_FragColor = vec4(col * (fresnel * 1.5 + 0.35), alpha);
  }
`;

const Core = ({ scroll, reduced }) => {
  const mesh = useRef();
  const shell = useRef();

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uAmp: { value: 0.16 },
      uColorA: { value: CYAN.clone() },
      uColorB: { value: VIOLET.clone() },
    }),
    []
  );

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const p = scroll.current; // 0 → 1 across the whole page

    if (!reduced) {
      uniforms.uTime.value = t;
    }
    // The form tightens and speeds up as you descend the page.
    uniforms.uAmp.value = 0.16 + p * 0.22;

    if (mesh.current) {
      mesh.current.rotation.y += delta * (reduced ? 0.02 : 0.08);
      mesh.current.rotation.x = p * 0.9;
      const s = 1 - p * 0.3;
      mesh.current.scale.setScalar(s);
    }
    if (shell.current) {
      shell.current.rotation.y -= delta * (reduced ? 0.01 : 0.05);
      shell.current.rotation.z = p * 0.6;
      shell.current.scale.setScalar(1.55 + p * 0.5);
    }
  });

  return (
    <group>
      <mesh ref={mesh}>
        <icosahedronGeometry args={[1.35, 18]} />
        <shaderMaterial
          uniforms={uniforms}
          vertexShader={vertexShader}
          fragmentShader={fragmentShader}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      <mesh ref={shell}>
        <icosahedronGeometry args={[1.35, 1]} />
        <meshBasicMaterial
          color={CYAN}
          wireframe
          transparent
          opacity={0.16}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  );
};

const Dust = ({ scroll, reduced }) => {
  const points = useRef();

  const geometry = useMemo(() => {
    const count = 1400;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      // Even-ish distribution through a spherical shell.
      const r = 3.2 + Math.random() * 5.5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return g;
  }, []);

  useFrame((state, delta) => {
    if (!points.current) return;
    points.current.rotation.y += delta * (reduced ? 0.004 : 0.016);
    points.current.rotation.x = scroll.current * 0.4;
  });

  return (
    <points ref={points} geometry={geometry}>
      <pointsMaterial
        size={0.028}
        color={new THREE.Color('#9FD9FF')}
        transparent
        opacity={0.75}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

const Rig = ({ scroll }) => {
  useFrame((state) => {
    const p = scroll.current;
    // Pull back and drift down as the page advances.
    state.camera.position.z = 5 + p * 2.4;
    state.camera.position.y = -p * 1.1;
    state.camera.lookAt(0, 0, 0);
  });
  return null;
};

const supportsWebGL = () => {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (err) {
    return false;
  }
};

const LandingScene = ({ scroll }) => {
  const ok = useMemo(supportsWebGL, []);
  const reduced = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // Without WebGL the page still reads: the CSS gradients behind this sit on their own.
  if (!ok) return null;

  return (
    <Canvas
      dpr={[1, 1.75]}
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
      camera={{ position: [0, 0, 5], fov: 45 }}
      aria-hidden="true"
    >
      <Core scroll={scroll} reduced={reduced} />
      <Dust scroll={scroll} reduced={reduced} />
      <Rig scroll={scroll} />
    </Canvas>
  );
};

export default LandingScene;
